import { useCallback, useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import CreatePasswordForm from '../create-password-form';
import CreatePasswordError from './CreatePasswordError';
import { sendTrackingEvent } from 'src/actions/search';
import LandingPageLayout from 'src/components/reusable-components/landing-page-layout';
import Spinner from 'src/components/spinner';
import useCopyPasteSteps from 'src/hooks/solve/useCopyPasteSteps';
import { selectTimeRange } from 'src/reducers/pageSettingsReducer/pageSettingsReducer';
import * as API from 'src/services/api';
import { useAppDispatch } from 'src/store/hooks';
import { Routes } from 'src/utils/enums';

const CreatePassword = () => {
  const { t } = useTranslation('authentication-wall');
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const { code, company_name: companyName, username } = queryString.parse(hash);
  const hasCredentials = !!(code && username);
  const dispatch = useAppDispatch();
  const timeRange = useSelector(selectTimeRange);
  const { resetCopyPasteSteps } = useCopyPasteSteps();

  const handleMagicLinkError = useCallback(() => {
    navigate({ hash: '' });
  }, [navigate]);

  const handleLogout = useCallback(async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      if (user) {
        await Auth.signOut();
        localStorage.removeItem('org');
        resetCopyPasteSteps();
      }
    } catch (error) {
      // noop
    }
  }, [resetCopyPasteSteps]);

  useEffect(() => {
    const init = async () => {
      await handleLogout();
      try {
        // If SSO is enabled for this org, redirect to sso login page
        const orgName = window.location.hostname.split('.')[0];
        const res = await API.getOrgDiscovery(orgName);
        if (res.is_sso_enabled) {
          navigate(Routes.SINGLE_SIGN_ON);
        }
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [navigate, handleLogout]);

  return (
    <LandingPageLayout>
      {isLoading ? (
        <Spinner />
      ) : hasCredentials ? (
        <CreatePasswordForm
          handleMagicLinkError={handleMagicLinkError}
          handleSuccess={() => {
            sendTrackingEvent(
              dispatch,
              'user-created-self-registration',
              timeRange,
              '',
              {
                company_name: companyName,
                username,
              },
            );
            navigate(Routes.ACCOUNT_SETTINGS);
          }}
          oldPassword={code.toString()}
          title={t('createPassword.title', {
            companyName: companyName || t('common.fallbackTeam'),
          })}
          username={username.toString()}
        />
      ) : (
        <CreatePasswordError />
      )}
    </LandingPageLayout>
  );
};

export default CreatePassword;
